import { createSharedComposable, useEventListener, useScriptTag } from "@vueuse/core"
import { useAnalytics } from "~/composables/analytics"

export { default as TavoButton } from "./TavoButton.vue"
export { default as TavoProvider } from "./TavoProvider.vue"

export const TavoClickUrl = "https://click.tavoreservations.com"
export const TavoEmbedUrl = import.meta.env.PUBLIC_TAVO_EMBED_URL || "http://localhost:4173/embed.js"

export interface TavoParams {
    group: string | number
    concept: string
    location?: string
}

export interface TavoConfig extends TavoWidget {
    group: string | number
    concept: string
    location?: string
    disabled?: boolean
}

export interface TavoWidget {
    fonts: {
        family: string
        src: string
        style: string
        weight: string
    }[]
    styles: {
        background?: string
        foreground?: string
        primary?: string
        primaryForeground?: string
        secondary?: string
        secondaryForeground?: string
        accent?: string
        accentForeground?: string
        destructive?: string
        destructiveForeground?: string
        muted?: string
        mutedForeground?: string
        loud?: string
        loudForeground?: string
        popover?: string
        popoverForeground?: string
        card?: string
        cardForeground?: string
        border?: string
        input?: string
        bodyFontFamily?: string
        headingFontFamily?: string
        submitButtonRadius?: string
    }
}

export type TavoWidgetEvent = {
    type: TavoWidgetEventTypeValue
} & ({
    type: typeof TavoWidgetEventType.Mounted | typeof TavoWidgetEventType.Ready
} | {
    type: typeof TavoWidgetEventType.Opened | typeof TavoWidgetEventType.Redirected | typeof TavoWidgetEventType.Changed | typeof TavoWidgetEventType.Submitted | typeof TavoWidgetEventType.Closed
    detail: TavoWidgetEventDetail
})

export const TavoWidgetEventType = {
    Mounted: "TavoWidgetMounted",
    Ready: "TavoWidgetReady",
    Opened: "TavoWidgetOpened",
    Redirected: "TavoWidgetRedirected",
    Changed: "TavoWidgetChanged",
    Submitted: "TavoWidgetSubmitted",
    Closed: "TavoWidgetClosed"
} as const
export type TavoWidgetEventTypeValue = typeof TavoWidgetEventType[keyof typeof TavoWidgetEventType]

export type TavoWidgetEventDetail = {
    traceId: number
    referrer: TavoWidgetReferrerValue
    originMode: TavoWidgetModeValue
    view: TavoWidgetViewValue
    mode: TavoWidgetModeValue
    stepIndex?: number
    duration?: number
}

export const TavoWidgetMode = {
    Reservation: "reservation",
    Inquiry: "inquiry"
} as const
export type TavoWidgetModeValue = typeof TavoWidgetMode[keyof typeof TavoWidgetMode]

export const TavoWidgetReferrer = {
    Reservation: "reservation",
    Catering: "catering",
    LargeParty: "large_party",
    PrivateDining: "private_dining"
} as const
export type TavoWidgetReferrerValue = typeof TavoWidgetReferrer[keyof typeof TavoWidgetReferrer]

export const TavoWidgetView = {
    Wizard: "wizard",
    Confirmation: "confirmation"
} as const
export type TavoWidgetViewValue = typeof TavoWidgetView[keyof typeof TavoWidgetView]

export const useTavo = createSharedComposable(() => {
    const { logEvent } = useAnalytics()
    const script = ref<HTMLScriptElement>()
    const loading = ref(false)
    const ready = ref(false)

    const { load } = useScriptTag(TavoEmbedUrl, undefined, {
        attrs: { type: "module" }
    })

    useEventListener(document, [
        TavoWidgetEventType.Ready,
        TavoWidgetEventType.Opened,
        TavoWidgetEventType.Redirected,
        TavoWidgetEventType.Changed,
        TavoWidgetEventType.Submitted,
        TavoWidgetEventType.Closed
    ], (e: TavoWidgetEvent) => {
        switch (e.type) {
            case TavoWidgetEventType.Ready: {
                ready.value = true
                break
            }
            case TavoWidgetEventType.Opened:
            case TavoWidgetEventType.Redirected:
            case TavoWidgetEventType.Changed:
            case TavoWidgetEventType.Submitted:
            case TavoWidgetEventType.Closed: {
                logEvent(e.type, e.detail)
                break
            }
        }
    })

    onMounted(async () => {
        if (!script.value) {
            loading.value = true
            script.value = (await load()) as HTMLScriptElement
            loading.value = false
        }
    })

    return {
        tavoScript: script,
        tavoLoading: readonly(loading),
        tavoReady: readonly(ready)
    }
})
